import React, { useEffect, useRef, useState } from 'react';

import infoSvg from '@assets/img/icons/info.svg';
import { TextInput } from '@components/admin/Forms';
import { Button } from '@components/Button';
import { ExpandMoreButton } from '@components/ExpandMoreButton';
import { CheckboxInput } from '@components/Forms/CheckboxInput';
import { useBookingCreateContext } from '@components/utilities/BookingInquiry/BookingCreateContext';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { receivingCityOptions } from '@data/cities';
import {
  availableServiceNames,
  routePlanServiceNameStrings,
  routePlanStrings,
} from '@data/routePlan';
import Collapse from '@material-ui/core/Collapse';
import { RoutePlanSimulator, ServiceName } from '@models/RoutePlan';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings, joinKeyPath } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useField } from 'formik';
import { intersection, sortBy, uniq } from 'lodash-es';
import { useDebounce } from 'react-use';
import useHoverDirty from 'react-use/lib/useHoverDirty';

const strings = createStrings({
  ja: {
    pickupDateInfo: () => (
      <>
        有料集荷サービスをご利用の場合、お客様ご自身で集荷日を指定することが可能です。
        <br />
        ご指定の日までにお荷物をご用意ください。
        <br />
        集荷日を起点として最短納品日(概算)を算出いたします。
        <br />
      </>
    ),
    moveInDateInfo: () => (
      <>
        有料集荷サービスをご利用しない場合、荷受倉庫への搬入予定日までに荷受倉庫へ商品をお送りいただく必要があります。
        <br />
        荷受倉庫への搬入予定日を起点として最短納品日(概算)を算出いたします。
        <br />
      </>
    ),
    filter: 'フィルター',
    editBookingInquiry: '予約内容を修正する',
    goToTop: 'TOPへ戻る',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  routePlanFilterContainer: {
    width: 340,
    padding: '24px 21px',
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.common.white,
  },
  hintContainer: {
    ...common.exSmallerText,
    ...common.textGray700,
    padding: 9,
    backgroundColor: '#F2F7F8',
    position: 'absolute',
    width: '100%',
    zIndex: 100,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  filterTitleContainer: {
    ...common.fontWeight600,
    ...common.textGray800,
    ...common.flexInline,
    ...common.justifyBetween,
    marginTop: 20,
    paddingBottom: 12,
    borderBottom: `1px solid ${theme.palette.base[300]}`,
  },
  filterSectionTitle: {
    ...common.fontWeight600,
    ...common.textGray800,
    ...common.flexInline,
    ...common.justifyBetween,
    marginBottom: 5,
  },
  buttonContainer: {
    ...common.flexColumn,
    rowGap: '0.5rem',
  },
  routePlanFilterWrapper: {
    ...common.flexColumn,
    rowGap: '0.5rem',
    maxHeight: '100%',
    overflow: 'scroll',
  },
  receivingCityGroupDisplayCities: {
    ...common.middleText,
    ...common.textGray700,
    ...common.flexColumn,
    paddingLeft: 23,
  },
}));

const FilterSection: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  const [open, setOpen] = useState(true);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div style={{ paddingTop: 10 }}>
      <div className={classes.filterSectionTitle}>
        {title}

        <ExpandMoreButton expanded={open} onClick={() => setOpen(!open)} />
      </div>

      <Collapse in={open}>
        <div className={classes.flexColumn} style={{ rowGap: '2px' }}>
          {children}
        </div>
      </Collapse>
    </div>
  );
};

export const RoutePlanFilter: React.FC<{
  name: string;
  routePlanSimulators: RoutePlanSimulator[];
  onGoBack: () => void;
}> = ({ name, routePlanSimulators, onGoBack }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { resolvePostBookingInquiryResult, setResolvePostBookingInquiryResult } =
    useBookingCreateContext();

  const { postBookingInquiry } = resolvePostBookingInquiryResult!;

  const infoRef = useRef<HTMLImageElement>(null);

  const isHoveringInfo = useHoverDirty(infoRef);

  const [hasPickupsField] = useField<'true' | 'false'>(joinKeyPath(name, 'has_pickups'));

  const [pickupDateField] = useField<string>(joinKeyPath(name, 'pickup_date'));

  const [moveInField] = useField<string>(joinKeyPath(name, 'move_in_date'));

  const receivingCities = sortBy(uniq(routePlanSimulators.map((r) => r.route_plan.receiving_city)));

  useDebounce(
    () => {
      if (hasPickupsField.value === 'true' && pickupDateField.value) {
        setResolvePostBookingInquiryResult({
          ...resolvePostBookingInquiryResult!,
          postBookingInquiry: {
            ...postBookingInquiry!,
            pickup_date: pickupDateField.value,
          },
        });
      } else if (moveInField.value) {
        setResolvePostBookingInquiryResult({
          ...resolvePostBookingInquiryResult!,
          postBookingInquiry: {
            ...postBookingInquiry!,
            move_in_date: moveInField.value,
          },
        });
      }
    },
    200,
    [pickupDateField.value, moveInField.value],
  );

  const [serviceNamesField, , serviceNamesHelpers] = useField<ServiceName[]>(
    joinKeyPath(name, 'service_names'),
  );

  const [receivingWarehouseCitiesField, , receivingWarehouseCitiesHelpers] = useField<string[]>(
    joinKeyPath(name, 'receiving_warehouse_cities'),
  );

  useEffect(() => {
    if (intersection(receivingWarehouseCitiesField.value, receivingCities).length === 0) {
      receivingWarehouseCitiesHelpers.setValue([]);
    }
  }, [routePlanSimulators, receivingCities.values]);

  const [filterInitialValues] = useState({
    service_names: serviceNamesField.value,
    receiving_warehouse_cities: receivingWarehouseCitiesField.value,
  });

  const hasPickups = hasPickupsField.value === 'true';

  const renderHint = () => (
    <div className={classes.hintContainer}>
      {hasPickups ? strings.pickupDateInfo() : strings.moveInDateInfo()}
    </div>
  );

  return (
    <div className={classes.routePlanFilterWrapper}>
      <div className={classes.routePlanFilterContainer}>
        <div
          className={clsx(classes.fontWeight600, classes.textGray800)}
          style={{ position: 'relative', marginBottom: '0.5rem' }}
        >
          {hasPickups ? bookingInquiryStrings.pickupDate : bookingInquiryStrings.moveInDate}

          <img
            ref={infoRef}
            src={infoSvg}
            alt="Info"
            style={{ cursor: 'help', marginLeft: 4, position: 'relative', top: 1 }}
          />

          {isHoveringInfo && renderHint()}
        </div>

        {hasPickups ? (
          <TextInput name="pickup_date" noLabel type="date" min={dayjs().format('YYYY-MM-DD')} />
        ) : (
          <TextInput name="move_in_date" noLabel type="date" min={dayjs().format('YYYY-MM-DD')} />
        )}

        <div style={{ height: 10 }} />

        <div className={classes.filterTitleContainer}>
          <div>{strings.filter}</div>

          <div
            className={clsx(classes.baseButton, classes.textPrimary, classes.exSmallerText)}
            onClick={() => {
              serviceNamesHelpers.setValue(filterInitialValues.service_names);
              receivingWarehouseCitiesHelpers.setValue(
                filterInitialValues.receiving_warehouse_cities,
              );
            }}
          >
            {formStrings.reset}
          </div>
        </div>

        <FilterSection title={routePlanStrings.serviceName}>
          {availableServiceNames.map((value) => (
            <CheckboxInput
              label={
                <>
                  <span className={classes.textGray700}>{routePlanServiceNameStrings[value]}</span>
                </>
              }
              value={value}
              name="service_names"
            />
          ))}
        </FilterSection>

        <FilterSection title={bookingInquiryStrings.warehouse}>
          {receivingCities.map((name) => {
            const option = receivingCityOptions.find((g) => g.name === name);
            return (
              <div key={name}>
                <CheckboxInput
                  label={<span className={classes.textGray700}>{option?.label}</span>}
                  value={name}
                  name="receiving_warehouse_cities"
                />
                <div className={classes.receivingCityGroupDisplayCities}>
                  {option?.displayCities.map((c) => (
                    <div key={c}>{c}</div>
                  ))}
                </div>
              </div>
            );
          })}
        </FilterSection>
      </div>

      <div className={classes.buttonContainer}>
        <Button
          text={strings.editBookingInquiry}
          color="outlined"
          size="small"
          type="button"
          onClick={onGoBack}
        />
        <Button
          text={strings.goToTop}
          color="primary"
          size="small"
          type="button"
          onClick={() => {
            document.getElementById('route-plan-list-container')!.scrollTo(0, 0);
          }}
        />
      </div>
    </div>
  );
};
